import { Component, Input, OnDestroy } from '@angular/core';
import { TagItem } from 'app/components/common/tag/tag.component';
import { AuthService } from 'app/services/auth.service';
import { ConfigService } from 'app/services/config.service';
import { DeviceRepository } from 'app/services/database/device.repository';
import { DeviceService } from 'app/services/device.service';
import { DevicesGroupService } from 'app/services/devicesGroup.service';
import { PresentationService } from 'app/services/presentation/presentation.service';
import { Logger } from 'app/shared/logger';
import { LoggerFactory } from 'app/shared/loggerFactory';
import { DeviceModel } from 'app/views/devices/models/device.model';
import { DeviceMetadataModel } from 'app/views/devices/models/deviceMetadata.model';
import { DevicesGroupModel } from 'app/views/devices/models/devicesGroup.model';
import * as moment from 'moment';
import { Observable, Observer } from 'rxjs';
import { Subscription } from 'rxjs/Subscription';

export class StatusRowViewModel {
    public deviceId: string;
    public name: string;
    public groupName: string;
    public group: TagItem;
    public state: TagItem;
    public currentPresentation: string;
    public nextPresentation: string;
    public nextPresentationStartDate: moment.Moment;

    constructor(data?: Partial<StatusRowViewModel>) {
        Object.assign(this, data);
    }
}

@Component({
    selector: 'psm-devices-status-table',
    templateUrl: 'devicesStatusTable.component.html',
    styleUrls: ['devicesStatusTable.component.scss']
})
export class DevicesStatusTableComponent implements OnDestroy {

    private groupNameByIds: { [s: string]: string; } = {};
    private logger: Logger;

    private statusesObserver: Observer<StatusRowViewModel[]>;
    public statuses: Observable<StatusRowViewModel[]>;

    private rows: StatusRowViewModel[] = [];
    private statusSubs: Subscription[] = [];
    private autoUpdateInterval;

    constructor(
        private loggerFactory: LoggerFactory,
        private deviceService: DeviceService,
        private deviceRepository: DeviceRepository,
        private devicesGroupService: DevicesGroupService,
        private presentationService: PresentationService,
        private authService: AuthService,
        private config: ConfigService
    ) {
        this.logger = this.loggerFactory.create('DeviceStatusTable');
    }

    public async ngOnInit() {
        let user = await this.authService.getCurrentUser();
        let uid = user.uid;

        let devices = await this.deviceService.getUserDeviceList(uid);
        let groups = await this.devicesGroupService.getUserDevicesGroups(uid);

        for (let group of groups) {
            this.groupNameByIds[group.id] = group.name;
        }

        for (let device of devices) {
            let row = await this.createDeviceTableRow(device);
            this.rows.push(row);
        }

        // Sort the rows
        this.rows.sort((a, b) => this.sortStatusRows(a, b));
        this.statuses = Observable.create(observer => {
            this.statusesObserver = observer;
            observer.next(this.rows);
        });

        let metadataUpdateDelay = await this.config.get('metadataUpdateDelay', 1000 * 60 * 5);
        let metadataValidityCycles = await this.config.get('metadataValidityCycles', 3);

        this.autoUpdateInterval = setInterval(async () => {
            this.logger.debug('Updating status table from interval');

            for (let row of this.rows) {
                let updated = await this.deviceRepository.get(row.deviceId);
                await this.updateDeviceStatus(updated, row);
            }
        }, metadataUpdateDelay * metadataValidityCycles);
    }

    private sortStatusRows(a: StatusRowViewModel, b: StatusRowViewModel): number {
        if (a.groupName > b.groupName) return 1;
        else if (a.groupName < b.groupName) return -1;

        if (a.name > b.name) return 1;
        else if (b.name < a.name) return -1;

        return 0;
    }

    private async createDeviceTableRow(device: DeviceModel) {
        let status = new StatusRowViewModel();
        await this.updateDeviceStatus(device, status, false);

        this.statusSubs.push(this.deviceRepository.watch('value', `${device.id}`)
            .subscribe(async val => {
                // Update the status on the value
                await this.updateDeviceStatus(val, status);
            }));

        return status;
    }

    private async updateDeviceStatus(device: DeviceModel, status: StatusRowViewModel, updateTable: boolean = true) {
        status.deviceId = device.id;
        status.name = device.name;
        status.group = await this.devicesGroupService.createDevicesGroupTag(device.groupId, false);
        status.groupName = this.groupNameByIds[device.groupId];

        if (!device.metadata) return status;

        status.state = await this.deviceService.createDeviceTag(device, false);

        let currentPresentation = device.metadata.currentPresentationId
            ? await this.presentationService.getPresentation(device.metadata.currentPresentationId)
            : undefined;

        let nextPresentation = device.metadata.nextPresentationId
            ? await this.presentationService.getPresentation(device.metadata.nextPresentationId)
            : undefined;

        status.currentPresentation = currentPresentation ? currentPresentation.name : undefined;
        status.nextPresentation = nextPresentation ? nextPresentation.name : undefined;
        status.nextPresentationStartDate = device.metadata.nextPresentationStart ? moment(device.metadata.nextPresentationStart) : undefined;

        // Update the table
        if (updateTable && this.statusesObserver) {
            this.statusesObserver.next(this.rows);
        }
    }

    public ngOnDestroy(): void {
        for (const sub of this.statusSubs) {
            sub.unsubscribe();
        }
        this.statusSubs = [];
        if (this.autoUpdateInterval) {
            clearInterval(this.autoUpdateInterval);
            this.autoUpdateInterval = undefined;
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/components/common/deviceStatusTable/devicesStatusTable.component.ts